/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { EventEmitter, Inject, Injectable, NgZone, Type, forwardRef } from "@angular/core";
import { MessageBus } from "../shared/message_bus";
import { Serializer, PRIMITIVE } from "../shared/serializer";

import { ServiceMessageBrokerRegistry } from "./service_message_broker_registry";
import { REGISTRY_CHANNEL, ON_WEB_WORKER, DEBUG } from "./api";
import { PostMessageBus, PostMessageBusSink, PostMessageBusSource } from "./post_message_bus";
import {
  ClientMessageBroker,
  ClientMessageBroker_,
  FnArg,
  FnTransferableArg,
  UiArguments,
} from "./client_message_broker";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class ServiceMessageBrokerFactory {
  /** @internal */
  _serializer: Serializer;
  _serviceRegistryBroker: ServiceMessageBroker = null;
  _clientRegistryBroker: ClientMessageBroker = null;
  private isDebug: boolean;
  private isWorker: boolean;
  private _registry: ServiceMessageBrokerRegistry;
  private _ngZone: NgZone;

  constructor(
    private _messageBus: MessageBus,
    _serializer: Serializer,
    @Inject(DEBUG) isDebug,
    @Inject(ON_WEB_WORKER) isWorker,
    _registry: ServiceMessageBrokerRegistry,
    _ngZone: NgZone,
  ) {
    this.isDebug = isDebug;
    this.isWorker = isWorker;
    this._registry = _registry;
    this._ngZone = _ngZone;
    this._serializer = _serializer;
  }

  createMessageBroker(
    channel: string,
    runInZone: boolean = true,
    hasTransfers?: boolean,
  ): ServiceMessageBroker {
    this._messageBus.initChannel(channel, runInZone, hasTransfers);
    let messageBroker = new ServiceMessageBroker_(this._messageBus, this._serializer, channel);
    messageBroker.isDebug = this.isDebug;
    // add a port based channel (will be used by all clients)
    let registryChannel = new MessageChannel();
    // client ports will be recieved over this port from app ServiceMessageBrokerFactory_
    let registryPort = registryChannel.port1;
    // sent to app ServiceMessageBrokerFactory_
    let servicePort = registryChannel.port2;

    registryPort.addEventListener("message", (event: MessageEvent) => {
      if (event.data && event.data.port) {
        let location = !!self ? `${self.location.hash}` : `index.html`;
        let clientPort = event.data.port;
        let sink = new PostMessageBusSink(clientPort);
        let source = new PostMessageBusSource(clientPort);
        let messageBus = new PostMessageBus(sink, source);
        messageBus.attachToZone(this._ngZone);
        messageBus.initChannel(channel, runInZone, hasTransfers);
        messageBroker.addSource(messageBus);

        // this is an information message
        let ackMsg = {
          channel: channel,
          type: "ackClientRegistration",
        };
        registryPort.postMessage(ackMsg);

        clientPort.start();

        if (this.isDebug) {
          logger.out.info(
            `ServiceMessageBroker on ${location} added port from registry to ${channel}`,
          );
        }
      }
    });

    registryPort.start();

    this._registry.registerServiceMessageBroker(channel, servicePort);

    return messageBroker;
  }
}

/**
 * Helper class for UIComponents that allows components to register methods.
 * If a registered method message is received from the broker on the worker,
 * the UIMessageBroker deserializes its arguments and calls the registered method.
 * If that method returns a promise, the UIMessageBroker returns the result to the worker.
 *
 * @experimental WebWorker support in Angular is currently experimental.
 */
export abstract class ServiceMessageBroker {
  abstract registerMethod(
    methodName: string,
    signature: any[],
    method: Function,
    returnType?: any,
    returnTransferable?: boolean,
    returnTransferableIsArray?: boolean,
  ): void;

  abstract addSource(messageBus);
}

export class ServiceMessageBroker_ extends ServiceMessageBroker {
  private _methods = new Map<string, Function>();
  public isDebug: boolean = false;

  constructor(messageBus: MessageBus, private _serializer: Serializer, public channel: string) {
    super();
    this.addSource(messageBus);
  }

  addSource(messageBus: MessageBus) {
    const sink = messageBus.to(this.channel);
    const source = messageBus.from(this.channel);
    source.subscribe({
      next: (message: any) => this._handleMessage(sink, message),
    });

    if (this.isDebug) {
      let location = !!self ? `${self.location.hash}` : "index.html";
      logger.out.info(
        `ServiceMessageBroker_ adding source to channel: ${this.channel} on ${location}`,
      );
    }
  }

  registerMethod(
    methodName: string,
    signature: any[],
    method: (..._: any[]) => Promise<any> | void,
    returnType?: any,
    returnTransferable?: boolean,
    returnTransferableIsArray?: boolean,
  ): void {
    this._methods.set(methodName, (sink: EventEmitter<any>, message: ReceivedMessage) => {
      const serializedArgs = message.args;
      const numArgs = signature ? signature.length : 0;
      const deserializedArgs = new Array(numArgs);
      for (let i = 0; i < numArgs; i++) {
        const serializedArg = serializedArgs[i];
        deserializedArgs[i] = this._serializer.deserialize(serializedArg, signature[i]);
      }

      const promise = method(...deserializedArgs);
      if (returnTransferableIsArray && returnType && promise) {
        this._wrapTransferableArrayWebWorkerPromise(sink, message.id, promise, returnType);
      } else if (returnTransferable && returnType && promise) {
        this._wrapTransferableWebWorkerPromise(sink, message.id, promise, returnType);
      } else if (returnType && promise) {
        this._wrapWebWorkerPromise(sink, message.id, promise, returnType);
      }
    });
  }

  private _handleMessage(sink: EventEmitter<any>, map: { [key: string]: any }): void {
    const message = new ReceivedMessage(map);
    if (this._methods.has(message.method)) {
      this._methods.get(message.method)(sink, message);
    } else {
      logger.out.warn(`serviceBroker._handleMessage method not found ${message.method}`);
    }
  }

  private _wrapWebWorkerPromise(
    sink: EventEmitter<any>,
    id: string,
    promise: Promise<any>,
    type: Type<any>,
  ): void {
    promise.then(
      (result: any) => {
        sink.emit({
          data: {
            type: "result",
            value: this._serializer.serialize(result, type),
            id: id,
          },
        });
      },
      (result: any) => {
        if (result instanceof Error) {
          result = result.message;
        } else if (typeof result !== "string") {
          result = null;
        }

        sink.emit({ data: { type: "error", value: result, id: id } });
      },
    );
  }

  private _wrapTransferableWebWorkerPromise(
    sink: EventEmitter<any>,
    id: string,
    promise: Promise<any>,
    type: Type<any>,
  ): void {
    promise.then(
      (result: any) => {
        sink.emit({
          data: {
            type: "result",
            value: this._serializer.serialize(result, type),
            id: id,
          },
          transfer: [result],
        });
      },
      (result: any) => {
        if (result instanceof Error) {
          result = result.message;
        } else if (typeof result !== "string") {
          result = null;
        }

        sink.emit({ data: { type: "error", value: result, id: id } });
      },
    );
  }

  private _wrapTransferableArrayWebWorkerPromise(
    sink: EventEmitter<any>,
    id: string,
    promise: Promise<any>,
    type: Type<any>,
  ): void {
    promise.then(
      (result: any) => {
        let transferables: Array<any>;

        if (Array.isArray(result)) {
          transferables = [...result];
        } else {
          transferables = [result];
        }

        sink.emit({
          data: {
            type: "result",
            value: this._serializer.serialize(result, type),
            id: id,
          },
          transfer: transferables,
        });
      },
      (result: any) => {
        if (result instanceof Error) {
          result = result.message;
        } else if (typeof result !== "string") {
          result = null;
        }

        sink.emit({ data: { type: "error", value: result, id: id } });
      },
    );
  }
}

/**
 * @experimental WebWorker support in Angular is currently experimental.
 */
export class ReceivedMessage {
  method: string;
  args: any[];
  id: string;
  type: string;

  constructor(data: { [key: string]: any }) {
    this.method = data["method"];
    this.args = data["args"];
    this.id = data["id"];
    this.type = data["type"];
  }
}
